import { RouteConfig } from 'vue-router';
import { useUserGetters, useUserActions } from '@/store';
import nexusClient from '@/apollo/nexusClient';
import {
  type AuthIsPortfolioCompleteQuery,
  type AuthIsPortfolioCompleteQueryVariables
} from '@/generated/nexus-graphql';

import { IS_PORTFOLIO_COMPLETE_QUERY } from '@/graphql';

// The typed UserActions helper currently only exposes a subset of actions. Cast to `any` to access
// the additional runtime actions until the store typing is updated.
const { getLatestDocumentByUserId } = (useUserActions as any)(['getLatestDocumentByUserId']);
const { getUserPermissions, clearProgramDoc } = (useUserActions as any)([
  'getUserPermissions',
  'clearProgramDoc'
]);
const routes: Array<RouteConfig> = [
  {
    path: '/mock-presentation',
    name: 'mockPresentation',
    component: () => import('@/components/molecules/deliverable/m-presentation.vue'),
    meta: {
      layout: 'no-nav'
    },
    props: {
      value: {
        data: {
          _id: 'mock-program-id',
          program_id: 'mock-program-id',
          adks: [
            {
              name: 'm-presentation',
              fields: [
                {
                  name: 'm-presentation',
                  value: {
                    submittedVideo: {},
                    submittedUrl: '',
                    youtubeOrDriveLink: ''
                  }
                },
                {
                  model: 'videoMaxLength',
                  setup: {
                    videoMaxLength: 10
                  },
                  preset: {
                    videoMaxLength: 10
                  }
                }
              ]
            }
          ]
        }
      },
      adkType: 'm-presentation',
      viewOnly: false,
      isMonitor: false,
      studentId: 'mock-student-id'
    }
  },
  {
    path: '/activity/demo',
    name: 'activityDemo',
    component: () => import('@/components/molecules/deliverable/m-presentation.vue'),
    meta: {
      layout: 'no-nav'
    },
    props: {
      value: {
        data: {
          _id: 'demo-program-id',
          program_id: 'demo-program-id',
          adks: [
            {
              name: 'm-presentation',
              fields: [
                {
                  name: 'm-presentation',
                  value: {
                    submittedVideo: {},
                    submittedUrl: '',
                    youtubeOrDriveLink: ''
                  }
                },
                {
                  model: 'videoMaxLength',
                  setup: {
                    videoMaxLength: 10
                  },
                  preset: {
                    videoMaxLength: 10
                  }
                }
              ]
            }
          ]
        }
      },
      adkType: 'm-presentation',
      viewOnly: false,
      isMonitor: false,
      studentId: 'demo-student-id'
    }
  },
  {
    path: '/temp-setup-program',
    name: 'tempSetupProgram',
    component: () => import('@/views/Guide/components/SetupWizard/SetupProgramWizard.vue'),
    meta: {
      layout: 'no-nav'
    },
    props: {
      website: 'example.com',
      value: {
        program_schema_version: '1.0',
        adks: [
          {
            name: 'Sample ADK',
            description: 'This is a sample ADK',
            type: 'text',
            required: true
          }
        ]
        // Add other fields as needed
      }
    }
  },
  {
    path: '/temp-setup-scopes',
    name: 'tempSetupScopes',
    component: () => import('@/views/Guide/components/SetupWizard/SetupScopesWizard.vue'),
    meta: {
      layout: 'no-nav'
    },
    props: {
      website: 'tesla.com',
      scopes: [
        'Scope 1. This is the first scope description  This is the first scope description  This is the first scope description  This is the first scope description  This is the first scope description  This is the first scope description ',
        'Scope 2 This is the second scope description This is the second scope description This is the second scope description This is the second scope description',
        'Scope 3 This is the third scope description This is the second scope description This is the second scope description This is the second scope description This is the second scope description This is the second scope description This is the second scope description This is the second scope description This is the second scope description This is the second scope description This is the second scope description This is the second scope description This is the second scope description This is the second scope description This is the second scope description'
      ],
      loading: false,
      value: ''
    }
  },
  {
    path: '/mentor',
    name: 'mentor',
    component: () => import('@/views/Mentor.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },

  {
    // RETIRE EVENTUALLY
    path: '/mentorship',
    name: 'mentorshipGuide',
    component: () => import('@/views/MentorshipGuide.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/guides/mentorship',
    name: 'mentorshipGuide',
    component: () => import('@/views/MentorshipGuide.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/match',
    name: 'match',
    component: () => import('@/views/MentorMatch.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/program/mentorship-match',
    name: 'findMatch',
    beforeEnter(to, from, next) {
      window.location.href =
        'https://pilotcity.com/public-program/-pilotcity-mentorship-network-686614734efa2a01fc708d5e';
    }
  },
  {
    path: '/airtable-test',
    name: 'airtableTest',
    component: () => import('@/views/AirtableTest.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/mentee',
    name: 'mentee',
    component: () => import('@/views/Mentee.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/b3diary',
    name: 'b3diary',
    component: () => import('@/views/B3Diary.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/adriane',
    name: 'adriane',
    component: () => import('@/views/Adriane.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/riddhi',
    name: 'riddhi',
    component: () => import('@/views/Riddhi.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/aprajit',
    name: 'aprajit',
    component: () => import('@/views/Aprajit.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/ai-feedback-demo',
    name: 'aiFeedbackDemo',
    component: () => import('@/views/AIFeedbackDemo.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/all-snackbar-messages',
    name: 'allSnackbarMessages',
    component: () => import('@/views/AllSnackbarMessages.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/ai-enhancement-test',
    name: 'aiEnhancementTest',
    component: () => import('@/views/AIEnhancementTest.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/ai-personalization-test',
    name: 'aiPersonalizationTest',
    component: () => import('@/views/AIPersonalizationTest.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/notification-test',
    name: 'notificationTest',
    component: () => import('@/views/NotificationTest.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/audio-test',
    name: 'audioTest',
    component: () => import('@/views/AudioTest.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/leon',
    name: 'leon',
    component: () => import('@/views/Leon.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/activity',
    name: 'ActivityWrapper',
    component: () => import('@/components/activities/activityWrapper.vue'),
    meta: {
      requiresAuth: true,
      layout: 'no-nav'
    }
  },
  {
    path: '/designtime',
    name: 'DesignActivity',
    component: () => import('@/components/activities/m-design/m-design.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/activity/showcase',
    name: 'ShowcaseActivity',
    component: () => import('@/components/activities/m-showcase/m-showcase.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/stage/activity/showcase/test',
    name: 'ShowcaseTest',
    component: () => import('@/views/ShowcaseTest.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/login/:page?',
    name: 'login',
    component: () => import(/* webpackChunkName: "Login" */ '@/views/Login.vue'),
    meta: {
      layout: 'no-nav'
    },
    beforeEnter(to, from, next) {
      const isSponsor: any = localStorage.getItem('isSponsor');
      const selectedOrg: any = localStorage.getItem('SelectedOrg');
      if (JSON.parse(isSponsor)) {
        next({ name: 'sponsor', query: { ...to.query } });
        return;
      }

      if (localStorage.getItem('setRoute') && JSON.parse(selectedOrg)) {
        const toPath = `${localStorage.getItem('setRoute')}?isVerified=true`;
        next({ path: toPath });
        return;
      }
      // if (localStorage.getItem('setRoute')) {
      //   const toPath = `${localStorage.getItem('setRoute')}`;
      //   next({ path: toPath });
      // }
      const { getUser } = useUserGetters(['getUser']);
      if (!getUser.value) {
        // push to landing with popup, preserve redirect param
        next({
          name: 'landing',
          params: { openLoginPopup: 'true' },
          query: { ...to.query }
        });
      } else {
        next({ name: 'portfolio' });
      }
    }
  },
  {
    path: '/signup/:page?',
    name: 'signup',
    component: () => import(/* webpackChunkName: "Signup" */ '@/views/Signup.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    },
    beforeEnter(to, from, next) {
      // const { getUser } = useUserGetters(['getUser']);
      // if (!getUser.value) next();
      // else next({ name: 'portfolio' });
      if (localStorage.getItem('apollo-token')) next({ name: 'portfolio' });
      else next();
    }
  },
  {
    path: '/monitor/:participantId?',
    name: 'Monitor',
    component: () => import(/* webpackChunkName: "Monitor" */ '@/views/Monitor/Monitor.vue'),
    meta: {
      requiresAuth: true,
      layout: 'no-nav'
    },
    async beforeEnter(to, from, next) {
      if (localStorage?.getItem('permissions')?.includes('can_monitor')) {
        next();
      } else {
        next({ path: '/' });
      }
    }
  },
  {
    path: '/:schemaType/form/:id',
    name: 'AddAdk',
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    },
    component: () => import(/* webpackChunkName: "Builder" */ '@/views/form-builder/Builder.vue')
  },
  {
    path: '/:schemaType/form/:id/:page',
    name: 'EditAdK',
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    },
    component: () => import(/* webpackChunkName: "Builder" */ '@/views/form-builder/Builder.vue')
  },
  {
    path: '/program',
    name: 'Monitor:actions',
    component: () => import(/* webpackChunkName: "Guide" */ '@/views/Guide/AirMonitor.vue'),

    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    }
  },
  {
    path: '/program/manage',
    name: 'Manage',
    component: () =>
      import(/* webpackChunkName: "Manage" */ '@/views/ManageProgram/views/Manage.vue')
  },
  {
    path: '/program/participants',
    name: 'Participants',
    component: () =>
      import(/* webpackChunkName: "Participants" */ '@/views/ManageProgram/views/Participants.vue')
  },
  {
    path: '/program/editprogram',
    name: 'Editprogram',
    component: () =>
      import(/* webpackChunkName: "EditProgram" */ '@/views/ManageProgram/views/EditProgram.vue')
  },
  {
    path: '/program/managestakeholder',
    name: 'ManageStakeholder',
    component: () =>
      import(
        /* webpackChunkName: "ManageStakeholder" */ '@/views/ManageProgram/views/ManageStakeholder.vue'
      )
  },
  {
    path: '/program2',
    name: 'ProgramManage',
    component: () =>
      import(/* webpackChunkName: "ProgramManage" */ '@/views/ManageProgram/ProgramManage.vue')
  },
  {
    path: '/ListView',
    name: 'Listview',
    component: () =>
      import(/* webpackChunkName: "ListView" */ '@/views/ManageProgram/components/ListView.vue')
  },
  {
    path: '/nav',
    name: 'Nav',
    component: () =>
      import(/* webpackChunkName: "Nav" */ '@/views/ManageProgram/components/Nav.vue')
  },
  {
    path: '/explore',
    name: 'explore',
    // component: () => import(/* webpackChunkName: "Explore" */ '@/views/Explore/Guide.vue'),
    component: () => import(/* webpackChunkName: "Explore" */ '@/views/Landing.vue'),
    meta: {
      layout: 'no-nav'
    },
    async beforeEnter(to, from, next) {
      if (localStorage?.getItem('permissions')?.includes('can_explore_programs')) {
        next();
      } else {
        next({ path: '/' });
      }
    }
  },
  {
    path: '/scope',
    name: 'scope',
    component: () =>
      import(/* webpackChunkName: "Explore" */ '@/components/molecules/m-build-scope.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    }
  },
  {
    path: '/guide/:programId/:page',
    name: 'guide',
    component: () => import(/* webpackChunkName: "Guide" */ '@/views/Guide/ParticipantGuide.vue'),

    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    },

    props: route => ({
      studentId: route.query.studentId,
      programId: route.params.programId,
      page: Number(route.params.page),
      monitorOrganizerView: route.query.monitorOrganizerView === 'true',
      previewView: route.query.preview === 'true'
    }),

    async beforeEnter(to, from, next) {
      clearProgramDoc();
      next();
    }
  },
  {
    path: '/program/:program_name',
    name: 'program',
    component: () => import(/* webpackChunkName: "Guide" */ '@/views/Guide/ParticipantSetup.vue'),

    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    },

    props: route => ({
      studentId: route.query.studentId,
      program_name: route.params.program_name,
      page: Number(route.params.page || 0),
      monitorOrganizerView: route.query.monitorOrganizerView === 'true',
      previewView: route.query.preview === 'true',
      adk: route.query.adk,
      type: 'program'
    })
  },
  {
    path: '/public-program/:program_name',
    name: 'publicProgram',
    component: () => import('@/views/Guide/Setup.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      showBasicUserInfo: true
    },
    props: route => ({
      studentId: route.query.studentId,
      program_name: route.params.program_name,
      page: Number(route.params.page || 0),
      monitorOrganizerView: route.query.monitorOrganizerView === 'true',
      previewView: route.query.preview === 'true',
      adk: route.query.adk,
      type: 'public-program'
    })
  },
  {
    path: '/demo-program/:program_name',
    name: 'demoProgram',
    component: () => import(/* webpackChunkName: "Guide" */ '@/views/Guide/Setup.vue'),

    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    },

    props: route => ({
      studentId: route.query.studentId,
      program_name: route.params.program_name,
      page: Number(route.params.page || 0),
      monitorOrganizerView: route.query.monitorOrganizerView === 'true',
      previewView: route.query.preview === 'true',
      adk: route.query.adk,
      type: 'demo-program'
    })
  },
  {
    path: '/:schemaType/details/:name',
    name: 'Details',
    component: () => import(/* webpackChunkName: "Guide" */ '@/views/Details.vue'),

    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    },

    props: route => ({
      program_name: route.params.name,
      schemaType: route.params.schemaType,
      isTemplate: route.params.isTemplate || route.params.schemaType === 'template'
    })
  },
  {
    path: '/guide',
    name: 'guide:airtime',
    component: () => import(/* webpackChunkName: "Guide" */ '@/views/Guide/AirGuide.vue'),

    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    }
  },
  {
    path: '/guide/:activityName',
    name: 'guide:activity',
    component: () => import(/* webpackChunkName: "Guide" */ '@/views/Guide/ActivityGuide.vue'),

    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    },

    props: route => ({
      activityName: route.params.activityName
    })
  },
  {
    path: '/portfolio/:component?/:profile?',
    name: 'portfolio',
    component: () => import(/* webpackChunkName: "Portfolio" */ '@/views/Portfolio/Portfolio.vue'),
    meta: {
      requiresAuth: true,
      requiresUser: true
    },
    props: () => ({
      isPortfolio: true
    }),
    async beforeEnter(to, from, next) {
      if (!to.params.component || to.params.component === '') {
        next({ path: '/portfolio/my-programs', replace: true });
        return;
      }
      // Honour any saved route overrides first – global guard already handled auth/user checks.
      const selectedOrg = localStorage.getItem('SelectedOrg');
      const setRoute = localStorage.getItem('setRoute');
      const demoRoute = localStorage.getItem('setDemoProgramRoute');

      if (setRoute && selectedOrg && JSON.parse(selectedOrg)) {
        next({ name: 'wallet' });
        return;
      }

      if (setRoute && setRoute !== to.path) {
        next({ path: setRoute });
        return;
      }

      if (demoRoute && demoRoute !== to.path) {
        next({ path: demoRoute });
        return;
      }

      next();
    }
  },
  {
    path: '/wallet',
    name: 'wallet',
    component: () => import(/* webpackChunkName: "Portfolio" */ '@/views/Portfolio/Portfolio.vue'),
    meta: {
      requiresAuth: true,
      requiresUser: true
    },
    props: () => ({
      isWallet: true
    })
  },
  {
    path: '/payment-wallet',
    name: 'payment-wallet',
    component: () => import(/* webpackChunkName: "Portfolio" */ '@/views/Portfolio/Portfolio.vue'),
    meta: {
      requiresAuth: true,
      requiresUser: true
    },
    props: () => ({
      isPaymentWallet: true
    })
  },
  {
    path: '/join/:orgId',
    name: 'pilotcityInvite',
    component: () =>
      import(/* webpackChunkName: "Portfolio" */ '@/views/Sponsor/components/PublicSponsor.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/profile-setting',
    name: 'profileSetting',
    component: () => import(/* webpackChunkName: "Portfolio" */ '@/views/Portfolio/Portfolio.vue'),
    meta: {
      requiresAuth: true,
      requiresUser: true
    },
    props: () => ({
      isSetting: true
    })
  },
  {
    path: '/badges',
    name: 'badges',
    component: () => import(/* webpackChunkName: "Portfolio" */ '@/views/Portfolio/Portfolio.vue'),
    meta: {
      requiresAuth: true,
      requiresUser: true
    },
    props: () => ({
      isBadges: true
    })
  },
  {
    path: '/citizen-profile',
    name: 'citizenProfile',
    component: () => import(/* webpackChunkName: "Portfolio" */ '@/views/Portfolio/Portfolio.vue'),
    meta: {
      requiresAuth: true,
      requiresUser: true
    },
    props: () => ({
      isCitizen: true
    })
  },
  {
    path: '/payment',
    name: 'payment',
    component: () => import(/* webpackChunkName: "Portfolio" */ '@/views/Portfolio/Portfolio.vue'),
    meta: {
      requiresAuth: true,
      requiresUser: true
    },
    props: () => ({
      isPayment: true
    })
  },
  {
    path: '/invite/:slug',
    name: 'invitation',
    component: () =>
      import(/* webpackChunkName: "Invite" */ '@/views/Portfolio/views/GlobalInvitation.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/setup',
    name: 'setup',
    component: () => import(/* webpackChunkName: "Setup" */ '@/views/Portfolio/SetupPortfolio.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: true
    },
    beforeEnter(to, from, next) {
      // If this flow includes an explicit redirect query, always allow navigation –
      // the SetupPortfolio component will handle final redirection after profile
      // completion. This prevents the guard from immediately sending new users
      // to the dashboard.
      if (to.query.redirect) {
        next();
        return;
      }

      const { isProfileComplete, getUser } = useUserGetters(['isProfileComplete', 'getUser']);
      const portfolioComplete = isProfileComplete.value === true;
      if (portfolioComplete) {
        if (localStorage.getItem('guardian-consent')) {
          next({
            name: 'sign',
            params: { studentId: `${localStorage.getItem('guardian-consent')}` }
          });
        } else if (localStorage.getItem('sponsorShipLinkPath')) {
          const sponsorShipLinkPath = `${localStorage.getItem('sponsorShipLinkPath')}`;
          next({ path: sponsorShipLinkPath });
        } else if (localStorage.getItem('isAutomationRoute')) {
          next({ name: 'landing' });
        } else if (localStorage.getItem('setRoute')) {
          const toPath = `${localStorage.getItem('setRoute')}`;
          next({ path: toPath });
        } else if (localStorage.getItem('setDemoProgramRoute')) {
          const toPath = `${localStorage.getItem('setDemoProgramRoute')}`;
          next({ path: toPath });
        } else if (getUser.value?._id) {
          // keep existing logic...
          next({ path: '/portfolio/my-programs' });
        }
      } else if (localStorage.getItem('setDemoProgramRoute')) {
        const toPath = `${localStorage.getItem('setDemoProgramRoute')}`;
        next({ path: toPath });
      } else {
        next();
      }
    }
  },
  {
    path: '/setup/:activityName',
    name: 'setup:activity',
    component: () => import(/* webpackChunkName: "Guide" */ '@/views/Guide/AirSetup.vue'),

    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    },

    props: route => ({
      activityName: route.params.activityName
    })
  },
  {
    path: '/balanceview',
    name: 'balanceview',
    component: () =>
      import(/* webpackChunkName: "BalanceView" */ '@/views/Portfolio/BalanceView.vue')
  },
  {
    path: '/create-program',
    name: 'createProgram',
    component: () => import(/* webpackChunkName: "CreateProgram" */ '@/views/CreateProgram.vue'),
    meta: {
      requiresAuth: true,
      requiresUser: true
    }
  },
  {
    path: '/ai-program-designer',
    name: 'aiProgramDesigner',
    component: () =>
      import(/* webpackChunkName: "CreateProgram" */ '@/views/AIProgramDesigner.vue'),
    meta: {
      requiresAuth: true
      // requiresUser: true
    }
  },
  {
    path: '/timeline',
    name: 'timeline',
    component: () => import(/* webpackChunkName: "Timeline" */ '@/components/Timeline.vue')
  },
  {
    path: '/sign/:studentId',
    name: 'sign',
    component: () => import(/* webpackChunkName: "Signup" */ '@/views/Agreement.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    },
    beforeEnter(to, from, next) {
      const { getUser } = useUserGetters(['getUser']);
      if (!getUser.value) {
        localStorage.setItem('guardian-consent', to.params.studentId);
        localStorage.setItem('is-guardian', to.params.studentId);
        next({
          name: 'login'
        });
      } else next();
    }
  },
  {
    path: '/',
    name: 'landing',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/Landing.vue'),
    props: route => ({
      preSelectedTemplate: route.query.template
    }),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/ecc2022',
    name: 'Ecc2022',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/Ecc2022.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/stemday',
    name: 'StemDay',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/StemDay.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/employer',
    name: 'EmployerLanding',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/EmployerLanding.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/help/employer',
    name: 'EmployerHelpLanding',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/EmployerHelpLanding.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/teacher',
    name: 'TeacherLanding',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/TeacherLanding.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/help/teacher',
    name: 'TeacherHelpLanding',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/TeacherHelpLanding.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/student',
    name: 'StudentLanding',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/StudentLanding.vue'),
    meta: {
      layout: 'no-nav'
    }
  },

  {
    path: '/stories',
    name: 'Stories',
    component: () => import(/* webpackChunkName: "Landing" */ '../views/Stories.vue'),
    meta: {
      layout: 'no-nav'
    }
  },

  {
    path: '/story',
    name: 'Story',
    component: () => import(/* webpackChunkName: "Landing" */ '../views/Story.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/show',
    name: 'Show',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/Show.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/help/student',
    name: 'StudentHelpLanding',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/StudentHelpLanding.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/parents',
    name: 'ParentLanding',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/ParentLanding.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/school',
    name: 'SchoolLanding',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/SchoolLanding.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/partner',
    name: 'PartnerLanding',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/PartnerLanding.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  // {
  //   path: '/sponsor',
  //   name: 'SponsorLanding',
  //   component: () => import(/* webpackChunkName: "Landing" */ '@/views/SponsorLanding.vue'),
  //   meta: {
  //     layout: 'no-nav'
  //   }
  // },
  {
    path: '/onboard2023',
    name: 'Onboard2023',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/Onboard2023.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/quote',
    name: 'SponsorPage',
    component: () => import('@/views/Sponsor.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    },
    props: route => ({
      email: route.query.email
      // example: http://localhost:8080/sponsor?email=dericklee@wehope.org
    })
  },
  {
    path: '/alumni',
    name: 'alumniJoin',
    component: () => import('@/views/AlumniForm.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/team',
    name: 'Alumni',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/Alumni.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/youtube',
    name: 'youtubeRedirect',
    beforeEnter() {
      window.location.href = 'https://youtube.com/pilotcity/shorts';
    }
  },
  // TEAM SAFETY, RULES, ETC
  {
    path: '/gate510safetytraining',
    name: 'Gate510safetytraining',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/Gate510safetytraining.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  // CONTRIBUTOR / ALUMNI TEAM ORIENTATION
  {
    path: '/orientation',
    name: 'AlumniTeamOrientation',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/AlumniTeamOrientation.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/contributor',
    name: 'contributor',
    component: () =>
      import(/* webpackChunkName: "Portfolio" */ '@/views/Portfolio/Contributor2.vue'),
    meta: {
      // requiresAuth: true,
      layout: 'no-nav'
      // requiresUser: true
    }
  },
  // EVENTS 2022
  {
    path: '/ebia2022',
    name: 'Ebia2022',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/Ebia2022.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/airtime2022recap',
    name: 'Airtime2022Recap',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/Airtime2022.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/airtime2022',
    name: 'Airtime2022',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/Airtime2022.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/sponsorpage',
    name: 'SponsorPage',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/SponsorPage.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/successteam',
    name: 'SuccessTeam',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/SuccessTeam.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/getsponsored',
    name: 'ApproveSponsor',
    component: () => import('@/views/ApproveSponsor.vue'),
    meta: {
      requiresAuth: true,
      layout: 'no-nav'
    }
  },
  {
    path: '/mobile',
    name: 'Mobile',
    component: () => import('@/views/Mobile.vue'),
    meta: {
      requiresAuth: true,
      layout: 'no-nav'
    }
  },
  {
    path: '/monitor-internship',
    name: 'MobileEmployer',
    component: () => import('@/views/MobileEmployer.vue'),
    meta: {
      requiresAuth: true,
      layout: 'no-nav'
    }
  },
  {
    path: '/sponsor/:shareCode',
    name: 'Sponsor',
    // component: () => import(/* webpackChunkName: "Guide" */ '@/views/Explore/Guide.vue'), // ! change this explore programs!!
    component: () => import(/* webpackChunkName: "Guide" */ '@/views/Landing.vue'), // ! change this explore programs!!
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    },
    // beforeEnter(to, from, next) {
    //   const { getUser } = useUserGetters(['getUser']);
    //   if (getUser.value) {
    //     next();
    //   } else {
    //     localStorage.setItem('sponsorShipLinkPath', to.path);
    //     next({ name: 'login' });
    //   }
    // },
    props: route => ({
      shareCode: route.params.shareCode
    })
  },
  {
    path: '/invite/:schoolName',
    name: 'invite',
    component: () => import(/* webpackChunkName: "Invite" */ '@/views/Invite.vue'),
    props: true,
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/invites/:partnerName',
    name: 'partnerInvite',
    component: () => import(/* webpackChunkName: "Invite" */ '@/views/PartnerInvite.vue'),
    props: true,
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/emailconfirmation',
    name: 'confirmEmail',
    component: () => import(/* webpackChunkName: "ConfirmEmail" */ '@/views/ConfirmEmail.vue'),
    props: route => ({
      token: route.query.token,
      tokenId: route.query.tokenId,
      username: route.query.username
    })
  },
  {
    path: '/changepassword',
    name: 'passwordChange',
    component: () => import(/* webpackChunkName: "ChangePassword" */ '@/views/ChangePassword.vue')
  },
  {
    path: '/discordconfirmation',
    name: 'confirmDiscord',
    component: () => import(/* webpackChunkName: "ConfirmDiscord" */ '@/views/ConfirmDiscord.vue'),
    props: route => ({
      code: route.query.code
    }),
    meta: {
      layout: 'no-nav',
      requiresAuth: false
    }
  },
  {
    path: '/password-reset',
    name: 'resetPassword',
    component: () => import(/* webpackChunkName: "ResetPassword" */ '@/views/ResetPassword.vue'),
    props: route => ({
      token: route.query.token,
      tokenId: route.query.tokenId
    })
  },
  {
    path: '/meetings',
    name: 'meetings',
    component: () => import(/* webpackChunkName: "Portfolio" */ '@/views/MeetingEvents.vue'),
    meta: {
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/privacy-policy',
    name: 'privacy-policy',
    component: () => import(/* webpackChunkName: "Portfolio" */ '@/views/PrivacyPolicy.vue')
  },
  {
    path: '/terms-and-conditions',
    name: 'terms-and-conditions',
    component: () => import(/* webpackChunkName: "Portfolio" */ '@/views/TermsAndConditions.vue')
  },
  {
    path: '/tour',
    name: 'tour',
    component: () => import(/* webpackChunkName: "Tour" */ '@/views/Tour.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/meet',
    name: 'meet',
    component: () => import(/* webpackChunkName: "Tour" */ '@/views/Tour.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/summer2025',
    name: 'summer2025Form',
    component: () => import('@/views/Summer2025Form.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/kaiser2025',
    name: 'kaiser2025Form',
    component: () => import('@/views/Kaiser2025Form.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/summeronboard',
    name: 'summerOnboardForm',
    component: () => import('@/views/SummerOnboardForm.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/why-join',
    name: 'whyJoin',
    component: () => import(/* webpackChunkName: "WhyJoin" */ '@/views/WhyJoin.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '*',
    name: '404',
    component: () => import(/* webpackChunkName: "404" */ '@/views/Error404.vue')
  },
  {
    path: '/authRequired',
    name: 'authError',
    component: () => import(/* webpackChunkName: "ErrorLogin" */ '@/views/ErrorLogin.vue')
  },
  {
    path: '/maintenance',
    name: 'maintenance',
    component: () => import(/* webpackChunkName: "maintenance" */ '@/views/Maintenance.vue'),
    meta: {
      requiresAuth: false,
      layout: 'no-nav'
    }
  },
  {
    path: '/template/:templateId/:page',
    name: 'templateSetup',
    component: () => import(/* webpackChunkName: "Guide" */ '@/views/Guide/TemplateSetup.vue'),

    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    },

    props: route => ({
      templateId: route.params.templateId,
      page: Number(route.params.page)
    })
  },
  {
    path: '/sponsor-programs/:user_id',
    name: 'sponsorProgramsPage',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/Landing.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    },
    props: route => ({
      userId: route.params.user_id,
      template: route.query.template,
      pathways: route.query.pathways,
      location: route.query.location
    })
  },
  {
    path: '/set-password',
    name: 'setPassword',
    component: () => import(/* webpackChunkName: "SetPassword" */ '@/views/SetPassword.vue'),
    props: route => ({
      token: route.query.token
    })
  },
  {
    path: '/sponsor/:shareCode/:id',
    name: 'EditSponsor',
    // component: () => import(/* webpackChunkName: "Guide" */ '@/views/Explore/Guide.vue'), // ! change this explore programs!!
    component: () => import(/* webpackChunkName: "Guide" */ '@/views/Landing.vue'), // ! change this explore programs!!
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    },
    // beforeEnter(to, from, next) {
    //   const { getUser } = useUserGetters(['getUser']);
    //   if (getUser.value) {
    //     next();
    //   } else {
    //     localStorage.setItem('sponsorShipLinkPath', to.path);
    //     next({ name: 'login' });
    //   }
    // },
    props: route => ({
      shareCode: route.params.shareCode,
      isEdit: route.params.id === 'edit'
    })
  },
  {
    path: '/temp-setup-site',
    name: 'tempSetupSite',
    component: () => import('@/views/Guide/components/SetupWizard/SetupSiteWizard.vue'),
    meta: {
      layout: 'no-nav'
    },
    props: {
      loading: false,
      errors: false
    }
  },
  {
    path: '/bulk-scope-setup',
    name: 'bulkScopeSetup',
    component: () => import('@/views/Guide/components/SetupWizard/BulkSetupScopesWizard.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/chuckie-talkie',
    name: 'chuckieTalkie',
    component: () => import('@/views/ChuckieTalkie.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      metaTags: [
        {
          name: 'apple-mobile-web-app-capable',
          content: 'yes'
        },
        {
          name: 'apple-mobile-web-app-status-bar-style',
          content: 'black-translucent'
        },
        {
          name: 'apple-mobile-web-app-title',
          content: 'Chuckie Talkie'
        }
      ]
    }
  },
  {
    path: '/organizations',
    name: 'organizations',
    component: () => import('@/views/Organizations/OrganizationsTable.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false
    }
  },
  {
    path: '/admin-program-link',
    name: 'adminProgramLink',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/Landing.vue'),
    props: route => ({
      preSelectedTemplate: route.query.template,
      isMasterAdmin: true
    }),
    meta: {
      layout: 'no-nav'
    }
  },

  {
    path: '/pricing',
    name: 'Pricing',
    component: () => import(/* webpackChunkName: "Landing" */ '../views/Pricing.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/sponsor',
    name: 'SponsorForm',
    component: () => import('@/views/Sponsor.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    },
    props: route => ({
      email: route.query.email
      // example: http://localhost:8080/sponsor?email=dericklee@wehope.org
    })
  },
  {
    path: '/numerology',
    name: 'numerology',
    component: () => import('@/views/Numerology.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/guide/organizer/:programId/:page',
    name: 'guide/organizer',
    component: () => import(/* webpackChunkName: "Guide" */ '@/views/Guide/OrganizerGuide.vue'),

    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    },

    props: route => ({
      studentId: route.query.studentId,
      programId: route.params.programId,
      page: Number(route.params.page),
      monitorOrganizerView: route.query.monitorOrganizerView === 'true',
      previewView: route.query.preview === 'true'
    }),

    async beforeEnter(to, from, next) {
      clearProgramDoc();
      next();
    }
  },
  {
    path: '/program/organizer/:program_name?',
    name: 'program/organizer',
    component: () => import(/* webpackChunkName: "Guide" */ '@/views/Guide/OrganizerSetup.vue'),

    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    },

    props: route => ({
      studentId: route.query.studentId,
      program_name: route.params.program_name,
      page: Number(route.params.page),
      monitorOrganizerView: route.query.monitorOrganizerView === 'true',
      previewView: route.query.preview === 'true',
      adk: route.query.adk,
      type: 'program'
    })
  },
  {
    path: '/sponsor-payment-mock',
    name: 'sponsor-payment-mock',
    component: () => import(/* webpackChunkName: "Guide" */ '@/views/SponsorPayment.vue'),

    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    },

    props: route => ({
      studentId: route.query.studentId || 'mock-student-123',
      id: route.query.id || 'mock-program-456',
      amount: route.query.amount || '150'
    })
  },
  {
    path: '/sponsor-payment',
    name: 'sponsor-payment',
    component: () => import(/* webpackChunkName: "Guide" */ '@/views/SponsorPayment.vue'),

    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    },

    props: route => ({
      studentId: route.query.studentId,
      id: route.query.id,
      amount: route.query.amount
    })
  },
  {
    path: '/test-sponsor-link',
    name: 'testSponsorLink',
    component: () => import('@/views/TestSponsorLink.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/topsecret-passcode',
    name: 'secretPasscode',
    component: () => import('@/views/SecretPasscode.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/topsecret',
    name: 'secret',
    component: () => import('@/views/SecretPage.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    },
    beforeEnter(to, from, next) {
      // Check if user has access to the secret page
      const hasAccess = localStorage.getItem('secretAccess');
      const accessTime = localStorage.getItem('secretAccessTime');

      if (hasAccess && accessTime) {
        // Check if access is still valid (24 hours)
        const now = Date.now();
        const accessTimeNum = parseInt(accessTime, 10);
        const twentyFourHours = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

        if (now - accessTimeNum < twentyFourHours) {
          next();
        } else {
          // Access expired, redirect to passcode entry
          localStorage.removeItem('secretAccess');
          localStorage.removeItem('secretAccessTime');
          localStorage.removeItem('usedPhoneNumber');
          next({ name: 'secretPasscode' });
        }
      } else {
        // No access, redirect to passcode entry
        next({ name: 'secretPasscode' });
      }
    }
  },
  {
    // RETIRE EVENTUALLY
    path: '/moderator',
    name: 'moderatorGuide',
    component: () => import('@/views/ModeratorGuide.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    // RETIRE EVENTUALLY
    path: '/guides/moderator',
    name: 'moderatorGuide',
    component: () => import('@/views/ModeratorGuide.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/submit',
    name: 'submitAirtable',
    component: () => import('@/views/SubmitAirtable.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    // RETIRE EVENTUALLY
    path: '/demo',
    name: 'demoGuide',
    component: () => import('@/views/DemoGuide.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/guides/demo',
    name: 'demoGuide',
    component: () => import('@/views/DemoGuide.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/guides/slides',
    name: 'slidesGuide',
    component: () => import('@/views/SlidesGuide.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/guides/files',
    name: 'filesGuide',
    component: () => import('@/views/FilesGuide.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/downtown',
    name: 'downtown',
    component: () => import('@/views/Downtown.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/finale',
    name: 'finale',
    beforeEnter() {
      window.location.href =
        'https://docs.google.com/document/d/1Pq3RlJMQVEwP1NbyaRTXqPkHTzMAX1e5vDuec_VOka8/edit?usp=sharing';
    }
  }
];

export default routes;
